import { create } from 'zustand';
import type { Token, CreateToken, UpdateToken } from '@machina/core';
import { createToken, snapToGrid, TOKEN_SIZE_SQUARES } from '@machina/core';
import { supabase, DbToken } from '../lib/supabase';
import { createSubscription } from '../lib/realtimeHelpers';
import { persistWithRetry, notifyPersistFailure } from '../lib/persistence';
import { useUndoStore } from './undoStore';

/**
 * Token Store
 *
 * Manages all tokens on the map. Handles selection, movement, and state updates.
 * Syncs with Supabase for multiplayer support.
 */

// Convert Token to DB format
function tokenToDb(token: Token, sceneId: string): Omit<DbToken, 'created_at' | 'updated_at'> {
  return {
    id: token.id,
    scene_id: sceneId,
    character_id: token.ownerId || null,
    data: {
      ...token,
      // Convert dates to ISO strings for JSON storage
      createdAt: token.createdAt instanceof Date ? token.createdAt.toISOString() : token.createdAt,
      updatedAt: token.updatedAt instanceof Date ? token.updatedAt.toISOString() : token.updatedAt,
    },
  };
}

// Convert DB format to Token
function dbToToken(db: DbToken): Token {
  const data = db.data as Record<string, unknown>;
  return {
    ...data,
    id: db.id,
    createdAt: new Date(data.createdAt as string),
    updatedAt: new Date(data.updatedAt as string),
  } as Token;
}

interface TokenState {
  // Token data
  tokens: Map<string, Token>;
  selectedIds: Set<string>;

  // Scene tracking for Supabase sync
  currentSceneId: string | null;
  /** Explicit setter — pushed from useSupabaseSync on scene activation */
  setCurrentSceneId: (sceneId: string | null) => void;
  realtimeCleanup: (() => void) | null;
  isLoading: boolean;

  /**
   * Set true when a Supabase write fails after all retries. Lets the UI /
   * reload paths know the in-memory state is ahead of the persisted state.
   */
  dirty: boolean;

  // Grid settings
  gridSize: number;

  // Actions
  addToken: (params: CreateToken) => Token;
  restoreToken: (token: Token) => void;  // For undo - restores with original id
  removeToken: (id: string) => void;
  updateToken: (params: UpdateToken) => void;
  moveToken: (id: string, to: { x: number; y: number }, snap?: boolean) => void;

  // Selection
  selectToken: (id: string, addToSelection?: boolean) => void;
  deselectToken: (id: string) => void;
  clearSelection: () => void;
  selectAll: () => void;
  selectTokensInRect: (rect: { x: number; y: number; width: number; height: number }) => void;

  // Queries
  getToken: (id: string) => Token | undefined;
  getTokenByCombatantId: (combatantId: string) => Token | undefined;
  getSelectedTokens: () => Token[];
  getTokensAtPosition: (position: { x: number; y: number }) => Token[];
  getTokensByOwner: (ownerId: string) => Token[];

  // Combat movement
  moveTowardsTarget: (attackerId: string, targetId: string) => void;

  // Bulk operations
  removeSelected: () => void;
  moveSelected: (delta: { x: number; y: number }) => void;

  // Grid
  setGridSize: (size: number) => void;

  // Sync with combat
  syncFromCombatant: (combatantId: string, data: Partial<Token>) => void;

  // Supabase sync
  loadFromSupabase: (sceneId: string) => Promise<void>;
  subscribeToRealtime: (sceneId: string) => void;
  unsubscribeFromRealtime: () => void;

  // Campaign isolation — wipes all in-memory tokens before a campaign switch
  // so the previous campaign's tokens aren't visible during the load gap.
  clearLocalState: () => void;
}

export const useTokenStore = create<TokenState>((set, get) => ({
  tokens: new Map(),
  selectedIds: new Set(),
  currentSceneId: null,
  setCurrentSceneId: (sceneId) => {
    const prev = get().currentSceneId;
    if (prev !== sceneId) {
      console.log(`[tokenStore] currentSceneId: ${prev ?? 'null'} → ${sceneId ?? 'null'}`);
      set({ currentSceneId: sceneId });
    }
  },
  realtimeCleanup: null,
  isLoading: false,
  dirty: false,
  gridSize: 50,

  // ==========================================================================
  // TOKEN CRUD
  // ==========================================================================

  addToken: (params) => {
    const token = createToken(params);

    // Snap to grid
    token.position = snapToGrid(token.position, get().gridSize);

    set((state) => {
      const newTokens = new Map(state.tokens);
      newTokens.set(token.id, token);
      return { tokens: newTokens };
    });

    // Sync to Supabase
    const { currentSceneId } = get();
    if (currentSceneId) {
      console.log('[Token] Syncing new token to Supabase:', token.name, 'scene:', currentSceneId);
      syncTokenToSupabase(token, currentSceneId);
    } else {
      console.warn('[Token] No currentSceneId - token NOT synced to Supabase:', token.name);
    }

    return token;
  },

  restoreToken: (token) => {
    const updatedToken = { ...token, updatedAt: new Date() };
    set((state) => {
      const newTokens = new Map(state.tokens);
      newTokens.set(token.id, updatedToken);
      return { tokens: newTokens };
    });

    // Sync to Supabase
    const { currentSceneId } = get();
    if (currentSceneId) {
      syncTokenToSupabase(updatedToken, currentSceneId);
    }
  },

  removeToken: (id) => {
    set((state) => {
      const newTokens = new Map(state.tokens);
      newTokens.delete(id);

      const newSelected = new Set(state.selectedIds);
      newSelected.delete(id);

      return { tokens: newTokens, selectedIds: newSelected };
    });

    // Sync to Supabase
    deleteTokenFromSupabase(id);
  },

  updateToken: (params) => {
    let updatedToken: Token | undefined;

    set((state) => {
      const token = state.tokens.get(params.id);
      if (!token) return state;

      updatedToken = {
        ...token,
        ...params,
        visuals: params.visuals
          ? { ...token.visuals, ...params.visuals }
          : token.visuals,
        updatedAt: new Date(),
      };

      const newTokens = new Map(state.tokens);
      newTokens.set(params.id, updatedToken);
      return { tokens: newTokens };
    });

    // Sync to Supabase
    const { currentSceneId } = get();
    if (currentSceneId && updatedToken) {
      syncTokenToSupabase(updatedToken, currentSceneId);
    }
  },

  moveToken: (id, to, snap = true) => {
    const { gridSize, currentSceneId } = get();
    let position = snap ? snapToGrid(to, gridSize) : to;

    // Clamp to canvas bounds — tokens always land within the map
    const maxPx = 200 * gridSize;
    position = {
      x: Math.max(0, Math.min(position.x, maxPx)),
      y: Math.max(0, Math.min(position.y, maxPx)),
    };

    let updatedToken: Token | undefined;

    set((state) => {
      const token = state.tokens.get(id);
      if (!token || token.locked) return state;

      updatedToken = {
        ...token,
        position,
        updatedAt: new Date(),
      };

      const newTokens = new Map(state.tokens);
      newTokens.set(id, updatedToken);
      return { tokens: newTokens };
    });

    // Sync to Supabase (debounced for smooth dragging)
    if (currentSceneId && updatedToken) {
      syncTokenToSupabase(updatedToken, currentSceneId);
    }
  },

  // ==========================================================================
  // SELECTION
  // ==========================================================================

  selectToken: (id, addToSelection = false) => {
    set((state) => {
      const token = state.tokens.get(id);
      if (!token) return state;

      const newSelected = addToSelection
        ? new Set(state.selectedIds)
        : new Set<string>();

      newSelected.add(id);

      // Update token selected state
      const newTokens = new Map(state.tokens);
      for (const [tokenId, t] of newTokens) {
        const shouldBeSelected = newSelected.has(tokenId);
        if (t.selected !== shouldBeSelected) {
          newTokens.set(tokenId, { ...t, selected: shouldBeSelected });
        }
      }

      return { tokens: newTokens, selectedIds: newSelected };
    });
  },

  deselectToken: (id) => {
    set((state) => {
      const newSelected = new Set(state.selectedIds);
      newSelected.delete(id);

      const token = state.tokens.get(id);
      if (token) {
        const newTokens = new Map(state.tokens);
        newTokens.set(id, { ...token, selected: false });
        return { tokens: newTokens, selectedIds: newSelected };
      }

      return { selectedIds: newSelected };
    });
  },

  clearSelection: () => {
    set((state) => {
      const newTokens = new Map(state.tokens);
      for (const [id, token] of newTokens) {
        if (token.selected) {
          newTokens.set(id, { ...token, selected: false });
        }
      }
      return { tokens: newTokens, selectedIds: new Set() };
    });
  },

  selectAll: () => {
    set((state) => {
      const newSelected = new Set<string>();
      const newTokens = new Map(state.tokens);

      for (const [id, token] of newTokens) {
        newSelected.add(id);
        if (!token.selected) {
          newTokens.set(id, { ...token, selected: true });
        }
      }

      return { tokens: newTokens, selectedIds: newSelected };
    });
  },

  selectTokensInRect: (rect) => {
    const { gridSize } = get();

    set((state) => {
      const newSelected = new Set<string>();
      const newTokens = new Map(state.tokens);

      for (const [id, token] of newTokens) {
        const size = TOKEN_SIZE_SQUARES[token.visuals.size] * gridSize;
        const tokenRight = token.position.x + size;
        const tokenBottom = token.position.y + size;

        const inRect =
          token.position.x < rect.x + rect.width &&
          tokenRight > rect.x &&
          token.position.y < rect.y + rect.height &&
          tokenBottom > rect.y;

        if (inRect) {
          newSelected.add(id);
          if (!token.selected) {
            newTokens.set(id, { ...token, selected: true });
          }
        } else if (token.selected) {
          newTokens.set(id, { ...token, selected: false });
        }
      }

      return { tokens: newTokens, selectedIds: newSelected };
    });
  },

  // ==========================================================================
  // QUERIES
  // ==========================================================================

  getToken: (id) => get().tokens.get(id),

  getTokenByCombatantId: (combatantId) => {
    const { tokens } = get();
    for (const token of tokens.values()) {
      if (token.combatantId === combatantId) {
        return token;
      }
    }
    return undefined;
  },

  getSelectedTokens: () => {
    const { tokens, selectedIds } = get();
    return Array.from(selectedIds)
      .map((id) => tokens.get(id))
      .filter((t): t is Token => t !== undefined);
  },

  getTokensAtPosition: (position) => {
    const { tokens, gridSize } = get();
    const result: Token[] = [];

    for (const token of tokens.values()) {
      const size = TOKEN_SIZE_SQUARES[token.visuals.size] * gridSize;
      if (
        position.x >= token.position.x &&
        position.x < token.position.x + size &&
        position.y >= token.position.y &&
        position.y < token.position.y + size
      ) {
        result.push(token);
      }
    }

    return result;
  },

  getTokensByOwner: (ownerId) => {
    const { tokens } = get();
    return Array.from(tokens.values()).filter((t) => t.ownerId === ownerId);
  },

  // ==========================================================================
  // BULK OPERATIONS
  // ==========================================================================

  removeSelected: () => {
    const { selectedIds, tokens, currentSceneId } = get();

    // Snapshot for undo BEFORE we delete anything. Capture full token data
    // and the linked mob data (the latter via mobStore lookup, since the
    // mob row will be deleted from DB by the cascade below).
    const snapshotTokens: Token[] = [];
    const mobIdsToRemove: string[] = [];
    for (const id of selectedIds) {
      const token = tokens.get(id);
      if (token) {
        snapshotTokens.push({ ...token });
        if (token.combatantId) mobIdsToRemove.push(token.combatantId);
      }
    }

    // Snapshot mobs by reading mobStore now (deferred import — avoid the
    // top-level circular dep that already exists between these stores).
    const snapshotMobsPromise = mobIdsToRemove.length > 0
      ? import('./mobStore').then(({ useMobStore }) => {
        const mobs = useMobStore.getState().mobs;
        const captured: import('./mobStore').Mob[] = [];
        for (const mid of mobIdsToRemove) {
          const m = mobs.get(mid);
          if (m) captured.push({ ...m });
        }
        return captured;
      })
      : Promise.resolve([] as import('./mobStore').Mob[]);

    // Sync each deletion to Supabase
    for (const id of selectedIds) {
      if (tokens.get(id)) deleteTokenFromSupabase(id);
    }
    set((state) => {
      const newTokens = new Map(state.tokens);
      for (const id of state.selectedIds) {
        newTokens.delete(id);
      }
      return { tokens: newTokens, selectedIds: new Set() };
    });

    // Push the undo entry once the mob snapshot resolves.
    void snapshotMobsPromise.then((snapshotMobs) => {
      const totalCount = snapshotTokens.length;
      const description = totalCount === 1
        ? `Removed ${snapshotTokens[0].name}`
        : `Removed ${totalCount} tokens`;

      useUndoStore.getState().pushDelete({
        type: 'bulk:remove',
        description,
        undo: async () => {
          // Restore mobs first (they own the combatantId). Use restoreMob
          // for any with a corresponding mob; tokens get restored via
          // restoreMob when paired, or directly if no mob.
          const mobModule = await import('./mobStore');
          const tokensByCombatantId = new Map<string, Token>();
          for (const t of snapshotTokens) {
            if (t.combatantId) tokensByCombatantId.set(t.combatantId, t);
          }

          const restoredCombatantIds = new Set<string>();
          for (const m of snapshotMobs) {
            const linkedToken = tokensByCombatantId.get(m.id) ?? null;
            await mobModule.useMobStore.getState().restoreMob(m, linkedToken, currentSceneId);
            restoredCombatantIds.add(m.id);
          }

          // Restore any tokens that didn't have a mob counterpart (PCs,
          // ad-hoc props, torches, etc.) or whose mob snapshot was missing.
          for (const t of snapshotTokens) {
            if (t.combatantId && restoredCombatantIds.has(t.combatantId)) continue;
            get().restoreToken(t);
          }
        },
        redo: async () => {
          // Re-select the snapshot tokens that still exist, then re-run.
          const have = new Set<string>();
          for (const t of snapshotTokens) {
            if (get().tokens.has(t.id)) have.add(t.id);
          }
          if (have.size === 0) return;
          set({ selectedIds: have });
          get().removeSelected();
        },
      });
    });

    // Clean up linked mobs from DB (deferred import to avoid circular dep).
    // skipUndo: bulk snapshot above already covers the undo entry — we don't
    // want each removed mob to overwrite the pill with its own description.
    if (mobIdsToRemove.length > 0) {
      import('./mobStore').then(async ({ useMobStore }) => {
        for (const mobId of mobIdsToRemove) {
          await useMobStore.getState().removeMob(mobId, { skipUndo: true });
        }
      });
    }
  },

  moveSelected: (delta) => {
    const { gridSize, currentSceneId } = get();

    const moved: Token[] = [];

    set((state) => {
      const newTokens = new Map(state.tokens);

      for (const id of state.selectedIds) {
        const token = newTokens.get(id);
        if (token && !token.locked) {
          const newPos = snapToGrid(
            {
              x: token.position.x + delta.x,
              y: token.position.y + delta.y,
            },
            gridSize
          );
          const updated = { ...token, position: newPos, updatedAt: new Date() };
          newTokens.set(id, updated);
          moved.push(updated);
        }
      }

      return { tokens: newTokens };
    });

    // Persist the new positions. Debounced so a flurry of keyboard nudges /
    // drag deltas coalesce into a single upsert instead of one write per pixel.
    if (currentSceneId && moved.length > 0) {
      enqueueTokenPositionSync(moved, currentSceneId);
    }
  },

  // ==========================================================================
  // COMBAT MOVEMENT
  // ==========================================================================

  moveTowardsTarget: (attackerCombatantId, targetCombatantId) => {
    const { tokens, gridSize, currentSceneId } = get();

    // Find attacker and target tokens by combatantId
    let attackerToken: Token | undefined;
    let targetToken: Token | undefined;

    for (const token of tokens.values()) {
      if (token.combatantId === attackerCombatantId) {
        attackerToken = token;
      }
      if (token.combatantId === targetCombatantId) {
        targetToken = token;
      }
      if (attackerToken && targetToken) break;
    }

    if (!attackerToken || !targetToken) return;
    if (attackerToken.locked) return;

    // Calculate direction from attacker to target
    const dx = targetToken.position.x - attackerToken.position.x;
    const dy = targetToken.position.y - attackerToken.position.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // If already adjacent (within 1.5 grid squares), don't move
    if (distance <= gridSize * 1.5) return;

    // Move to position adjacent to target (1 grid square away)
    // Calculate unit vector and position one grid square from target
    const ux = dx / distance;
    const uy = dy / distance;

    const newX = targetToken.position.x - ux * gridSize;
    const newY = targetToken.position.y - uy * gridSize;

    // Snap to grid
    const snappedPos = snapToGrid({ x: newX, y: newY }, gridSize);

    let movedToken: Token | undefined;

    set((state) => {
      const newTokens = new Map(state.tokens);
      movedToken = {
        ...attackerToken!,
        position: snappedPos,
        updatedAt: new Date(),
      };
      newTokens.set(attackerToken!.id, movedToken);
      return { tokens: newTokens };
    });

    // Persist the combat move so it survives reload and reaches other clients.
    // Debounced to coalesce the rapid repositions during a battle-sim turn.
    if (currentSceneId && movedToken) {
      enqueueTokenPositionSync([movedToken], currentSceneId);
    }
  },

  // ==========================================================================
  // GRID
  // ==========================================================================

  setGridSize: (size) => set({ gridSize: size }),

  // ==========================================================================
  // COMBAT SYNC
  // ==========================================================================

  syncFromCombatant: (combatantId, data) => {
    set((state) => {
      const newTokens = new Map(state.tokens);

      for (const [id, token] of newTokens) {
        if (token.combatantId === combatantId) {
          newTokens.set(id, {
            ...token,
            ...data,
            updatedAt: new Date(),
          });
        }
      }

      return { tokens: newTokens };
    });
  },

  // ==========================================================================
  // SUPABASE SYNC
  // ==========================================================================

  loadFromSupabase: async (sceneId) => {
    set({ isLoading: true });
    try {
      const { data, error } = await supabase
        .from('machina_tokens')
        .select('*')
        .eq('scene_id', sceneId);

      if (error) throw error;

      if (get().currentSceneId !== sceneId) {
        console.log('[tokenStore] Scene changed during load, discarding results');
        set({ isLoading: false });
        return;
      }

      if (data && data.length > 0) {
        const newTokens = new Map<string, Token>();
        for (const row of data) {
          const token = dbToToken(row as DbToken);
          newTokens.set(token.id, token);
        }
        set({
          tokens: newTokens,
          currentSceneId: sceneId,
          isLoading: false,
        });
      } else {
        set({
          tokens: new Map(),
          currentSceneId: sceneId,
          isLoading: false,
        });
      }
    } catch (err) {
      console.error('Failed to load tokens:', err);
      set({ isLoading: false });
    }
  },

  clearLocalState: () => {
    set({
      tokens: new Map(),
      selectedIds: new Set(),
      currentSceneId: null,
      isLoading: false,
    });
  },

  subscribeToRealtime: (sceneId) => {
    get().unsubscribeFromRealtime();

    const { cleanup } = createSubscription({
      channelName: `tokens:${sceneId}`,
      table: 'machina_tokens',
      filter: `scene_id=eq.${sceneId}`,
      event: '*',
      onPayload: (payload) => {
        const { tokens } = get();

        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          const token = dbToToken(payload.new as unknown as DbToken);
          const existing = tokens.get(token.id);
          if (existing && existing.updatedAt >= token.updatedAt) return;
          const newTokens = new Map(tokens);
          newTokens.set(token.id, token);
          set({ tokens: newTokens });
        } else if (payload.eventType === 'DELETE') {
          const deletedId = (payload.old as { id: string }).id;
          if (tokens.has(deletedId)) {
            const newTokens = new Map(tokens);
            newTokens.delete(deletedId);
            set({ tokens: newTokens });
          }
        }
      },
    });

    set({ realtimeCleanup: cleanup, currentSceneId: sceneId });
  },

  unsubscribeFromRealtime: () => {
    const { realtimeCleanup } = get();
    if (realtimeCleanup) {
      realtimeCleanup();
      set({ realtimeCleanup: null });
    }
  },
}));

// =============================================================================
// SYNC HELPERS (call after local mutations to sync to Supabase)
// =============================================================================

/**
 * Save a token to Supabase (upsert).
 *
 * Wrapped in persistWithRetry so a failed write retries once and then surfaces
 * a sticky failure toast instead of dying silently in a console.error. On final
 * failure the store is flagged `dirty` so the in-memory state is known to be
 * ahead of what's persisted.
 */
export async function syncTokenToSupabase(token: Token, sceneId: string): Promise<void> {
  const dbToken = tokenToDb(token, sceneId);
  await persistWithRetry(
    'token',
    async () => await supabase.from('machina_tokens').upsert(dbToken, { onConflict: 'id' }),
    { onFinalFailure: () => useTokenStore.setState({ dirty: true }) },
  );
}

/**
 * Delete a token from Supabase.
 *
 * Wrapped in persistWithRetry so a failed delete retries once and then surfaces
 * a sticky failure toast instead of dying silently in a console.error.
 */
export async function deleteTokenFromSupabase(tokenId: string): Promise<void> {
  await persistWithRetry(
    'token deletion',
    async () => await supabase.from('machina_tokens').delete().eq('id', tokenId),
    { onFinalFailure: () => useTokenStore.setState({ dirty: true }) },
  );
}

// =============================================================================
// DEBOUNCED POSITION SYNC
// =============================================================================
//
// moveSelected (keyboard nudges / drag) and moveTowardsTarget (battle-sim) can
// fire many position updates in quick succession. Writing each one to Supabase
// would flood the network and race. Instead we coalesce pending moves per token
// (keeping the latest position) and flush them in a single batched upsert after
// a short quiet window.

const PENDING_POSITION_DEBOUNCE_MS = 400;

/** Latest pending token per id, keyed so rapid moves of the same token coalesce. */
const pendingPositionTokens = new Map<string, Token>();
let pendingPositionSceneId: string | null = null;
let positionFlushTimer: ReturnType<typeof setTimeout> | null = null;

function enqueueTokenPositionSync(tokens: Token[], sceneId: string): void {
  pendingPositionSceneId = sceneId;
  for (const token of tokens) {
    pendingPositionTokens.set(token.id, token);
  }

  if (positionFlushTimer) clearTimeout(positionFlushTimer);
  positionFlushTimer = setTimeout(() => {
    void flushTokenPositionSync();
  }, PENDING_POSITION_DEBOUNCE_MS);
}

async function flushTokenPositionSync(): Promise<void> {
  positionFlushTimer = null;

  if (pendingPositionTokens.size === 0) return;

  const sceneId = pendingPositionSceneId;
  const queued = Array.from(pendingPositionTokens.values());
  pendingPositionTokens.clear();

  if (!sceneId) {
    // Scene id vanished between enqueue and flush (e.g. campaign switch). The
    // moves can't be persisted — make sure that's not silent and flag dirty.
    useTokenStore.setState({ dirty: true });
    notifyPersistFailure('token positions', new Error('no active scene to persist to'));
    return;
  }

  const rows = queued.map((t) => tokenToDb(t, sceneId));

  await persistWithRetry(
    'token positions',
    async () => await supabase.from('machina_tokens').upsert(rows, { onConflict: 'id' }),
    { onFinalFailure: () => useTokenStore.setState({ dirty: true }) },
  );
}
